import React, { useState } from 'react';
import { useMap } from 'react-use';

interface DistributedState<SubState, Summary> {
  useHostState(): {
    Provider: React.FC;
    summary: Summary;
  };
  useChildState(value: SubState): void;
}
export function createDistributedState<SubState, Summary>(
  summarize: (subStates: SubState[]) => Summary,
): DistributedState<SubState, Summary>;
export function createDistributedState<SubState>(): DistributedState<
  SubState,
  SubState[]
>;
export function createDistributedState<SubState, Summary>(
  // Per the two overloads above, if summarize is not passed it must return an array of SubState and therefore this default is valid
  summarize: (subStates: SubState[]) => Summary = (a => a as unknown as Summary),
): DistributedState<SubState, Summary> {
  /** Context holds all the child states inside a map, indexed by a unique key to ensure they can each edit/delete their own state safely */
  const Ctx = React.createContext<{
    set: (key: symbol, newState: SubState) => void;
    clear: (key: symbol) => void;
  }>({
    set: () => undefined,
    clear: () => undefined,
  });

  function useHostState() {
    const [state, { set, remove }] = useMap<Record<symbol, SubState>>();

    const contextValue = React.useMemo(
      () => ({
        set: (key: symbol, state: SubState) => set(key, state),
        clear: (key: symbol) => remove(key),
      }),
      [set, remove],
    );

    const Provider = React.useCallback(
      ({ children }) => (
        <Ctx.Provider value={contextValue}>{children}</Ctx.Provider>
      ),
      [contextValue],
    );

    const states = React.useMemo(
      () => Object.getOwnPropertySymbols(state).map(key => state[key]),
      [state],
    );
    const summary = React.useMemo(() => summarize(states), [states]);
    return {
      /** Wrap your children in this provider to collect their states */
      Provider,
      /** A list of all the individual child states */
      state,
      /** A summary of all the individual child states, as calculated by the summarization function */
      summary,
    };
  }

  function useChildState(value: SubState) {
    const [key] = useState(
      Symbol('Unique symbol for this specific instance of the hook'),
    );

    const { set, clear } = React.useContext(Ctx);

    React.useEffect(() => {
      set(key, value);
    }, [value, key, set, clear]);

    React.useEffect(() => {
      return () => clear(key);
    }, [clear, key]);
  }

  return { useHostState, useChildState };
}
